/**
 * Referral Code Context
 *
 * Global context for detecting and managing referral codes across the entire app.
 * Detects referral codes from URL parameters on ANY page and stores them for later use.
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getReferralCodeFromUrl,
  storeReferralCodeForUser,
  getStoredReferralCode,
} from '../utils/referralUtils';

interface ReferralCodeContextType {
  referralCode: string | null;
  hasReferralCode: boolean;
  clearReferralCode: () => void;
}

const ReferralCodeContext = createContext<ReferralCodeContextType | undefined>(undefined);

interface ReferralCodeProviderProps {
  children: ReactNode;
}

export const ReferralCodeProvider: React.FC<ReferralCodeProviderProps> = ({ children }) => {
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    // Check for referral code in URL on any page
    const urlRefCode = getReferralCodeFromUrl();

    if (urlRefCode) {
      // Store the referral code
      storeReferralCodeForUser(urlRefCode);
      setReferralCode(urlRefCode);
    } else {
      // Check if we already have a stored referral code
      const storedCode = getStoredReferralCode();
      if (storedCode) {
        setReferralCode(storedCode);
      }
    }
  }, [location.search]);

  const clearReferralCode = () => {
    setReferralCode(null);
  };

  const value: ReferralCodeContextType = {
    referralCode,
    hasReferralCode: !!referralCode,
    clearReferralCode,
  };

  return (
    <ReferralCodeContext.Provider value={value}>
      {children}
    </ReferralCodeContext.Provider>
  );
};

export const useReferralCode = (): ReferralCodeContextType => {
  const context = useContext(ReferralCodeContext);
  if (context === undefined) {
    throw new Error('useReferralCode must be used within a ReferralCodeProvider');
  }
  return context;
};
